import { Injectable, inject } from '@angular/core';
import {
  DetectionService,
  RenderService,
  SimulationService,
  TaskService,
} from '@services';
import '@tensorflow/tfjs-backend-webgl';
import { PrecisionCuttingMetricsService } from './precision-cutting-metrics.service';
import { SharedService } from './shared.service';

const WORKERS = {
  cycleWorker: {
    id: 'cycleWorker',
    model: 'default',
    historySize: 40,
    confidence: 0.1,
  },
  cutCheckWorker: {
    id: 'cutCheckWorker',
    model: 'cutCheck',
    historySize: 40,
    confidence: 0.1,
  },
};

@Injectable({
  providedIn: 'root',
})
export class PrecisionCuttingRightService {
  private detectionService = inject(DetectionService);
  private precisionCuttingMetricsService = inject(
    PrecisionCuttingMetricsService,
  );
  private renderService = inject(RenderService);
  private shared = inject(SharedService);
  private simulationService = inject(SimulationService);
  private taskService = inject(TaskService);

  constructor() {
    this.detectionService.addWorkers(WORKERS);
    this.shared.cycleWorkerId = WORKERS.cycleWorker.id;
    this.shared.cutCheckWorkerId = WORKERS.cutCheckWorker.id;
  }

  async runDetection() {
    if (this.simulationService.isSimulationRunning()) {
      await this.detectionService.detect(WORKERS.cycleWorker.id);
      await this.detectionService.detect(WORKERS.cutCheckWorker.id);
      if (this.simulationService.state() === 'kit') {
        this.shared.checkSample();
      } else if (this.simulationService.state() === 'simulation') {
        this.shared.updateHistoryOfCuts();
        this.shared.toolTracking();
        switch (this.taskService.currentTaskId()) {
          case 'positionCircle': {
            this.shared.checkSample().then(async (isConfirmed) => {
              if (isConfirmed) {
                await this.renderService.waitForStaticCleanup();
                await this.taskService.completeTask('positionCircle');
              }
            });
            break;
          }
          case 'checkInstruments': {
            this.shared.checkInstruments().then(async (isConfirmed) => {
              if (isConfirmed) {
                await this.renderService.waitForStaticCleanup();
                await this.taskService.completeTask('checkInstruments');
              }
            });
            break;
          }
          case 'firstCut': {
            this.shared.firstCut().then(async (isConfirmed) => {
              if (isConfirmed) {
                await this.taskService.completeTask('firstCut');
              }
            });
            break;
          }
          case 'secondCut': {
            this.shared.secondCut().then(async (isConfirmed) => {
              if (isConfirmed) {
                await this.taskService.completeTask('secondCut');
              }
            });
            break;
          }
          case 'positionCut': {
            if (!this.taskService.taskListCompleted()) {
              this.shared.positionCut().then(async (isConfirmed) => {
                if (isConfirmed) {
                  await this.taskService.completeTask('positionCut');
                }
              });
            }
            break;
          }
        }
      }
    }
  }

  stopDetection() {
    this.precisionCuttingMetricsService.finalizeMetrics();
    this.detectionService.terminateAllworkers();
  }
}
